import { buildTopLevelRows, buildChildRows, buildLaborSubRows, monthKeyToField } from './schedule-five-cost-code-helpers';

const SECTION_COST_CODE_MAP = {
    equipment: 1,
    material: 2,
    subcontractors: 7,
    otherCosts: 8,
    generalConditions: 9,
};

const getMonthColumns = rawData => {
    const keys = [...new Set(rawData.map(r => r.monthKey))].sort((a, b) => a - b);
    return keys.map(k => monthKeyToField(k));
};

const cell = (value, bold = false) => ({ value, bold });

const dataValues = (entry, monthColumns) => [cell(entry.total), ...monthColumns.map(m => cell(entry.Values[m] ?? ''))];

const buildHeaderRow = monthColumns => ({
    cells: [cell('', true), cell('', true), cell('', true), cell('Total', true), ...monthColumns.map(m => cell(m, true))],
});

// Section row: col 0 = section name (bold), col 1-2 empty, then data
const buildSectionRow = (name, entry, monthColumns) => ({
    cells: [cell(name, true), cell(''), cell(''), ...dataValues(entry, monthColumns)],
});

// Sub-type row: col 0 empty, col 1 = type name (bold), col 2 empty, then data
const buildSubTypeRow = (name, entry, monthColumns) => ({
    cells: [cell(''), cell(name, true), cell(''), ...dataValues(entry, monthColumns)],
});

// Companion row (FTE): same column layout as its parent, data only
const buildCompanionRow = (entry, monthColumns) => {
    const cols = [cell(''), cell(''), cell('')];
    return { cells: [...cols, ...dataValues(entry, monthColumns)] };
};

// Project row: col 0-1 empty, col 2 = project label, then data
const buildProjectRow = (entry, monthColumns) => ({
    cells: [cell(''), cell(''), cell(`${entry.projectNumber} - ${entry.projectName}`), ...dataValues(entry, monthColumns)],
});

const buildProjectRows = (rawData, costCode, monthColumns) => {
    const children = buildChildRows(rawData, costCode);
    const rows = [];
    for (const child of children) {
        if (child.isSubRow) {
            rows.push(buildCompanionRow(child, monthColumns));
        } else {
            rows.push(buildProjectRow(child, monthColumns));
        }
    }
    return rows;
};

export const buildCostCodeSheet = rawData => {
    const monthColumns = getMonthColumns(rawData);
    const rows = [buildHeaderRow(monthColumns)];

    if (rawData.length === 0) return { title: 'Cost Code', rows };

    const topLevelRows = buildTopLevelRows(rawData);

    for (let i = 0; i < topLevelRows.length; i++) {
        const entry = topLevelRows[i];
        if (entry.isSubRow) continue;

        const section = entry.section;
        const isLaborHours = section === 'laborHours';
        const isLaborCosts = section === 'laborCosts';
        const fteCompanion = topLevelRows[i + 1]?.isSubRow ? topLevelRows[i + 1] : null;

        if (isLaborHours || isLaborCosts) {
            const subRows = buildLaborSubRows(rawData, isLaborCosts);
            const activeSubRows = subRows.filter(r => !r.isSubRow && r.total !== 0);
            if (activeSubRows.length === 0) continue;

            rows.push(buildSectionRow(entry.name, entry, monthColumns));
            if (fteCompanion) {
                rows.push(buildCompanionRow(fteCompanion, monthColumns));
            }

            for (let j = 0; j < subRows.length; j++) {
                const sub = subRows[j];
                if (sub.isSubRow) continue;
                if (sub.total === 0) continue;

                const subFte = subRows[j + 1]?.isSubRow ? subRows[j + 1] : null;
                const projectRows = buildProjectRows(rawData, sub.costCode, monthColumns);
                if (projectRows.length === 0) continue;

                rows.push(buildSubTypeRow(sub.name, sub, monthColumns));
                if (subFte) {
                    rows.push(buildCompanionRow(subFte, monthColumns));
                }
                rows.push(...projectRows);
            }
        } else {
            const costCode = SECTION_COST_CODE_MAP[section];
            const projectRows = buildProjectRows(rawData, costCode, monthColumns);
            if (projectRows.length === 0) continue;

            rows.push(buildSectionRow(entry.name, entry, monthColumns));
            rows.push(...projectRows);
        }
    }

    return { title: 'Cost Code', rows };
};
